import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Divider,
  Box,
} from "@material-ui/core";
import ExerciseSelector from "./ExerciseSelector";
import ExerciseList from "../views/ExerciseList";
import ExerciseFilterHeader from "./ExerciseSelectorHeader";
import exerciseConstants from "constants/Exercises/exerciseConstants";
import { EXERCISE_MODULES } from "../exerciseModule";
import { useExerciseFilter } from "../hooks/useExerciseFilter";

/**
 * Componente seletor de exercícios para o perfil de Usuário (Aluno).
 *
 * Permite visualizar e selecionar exercícios de acordo com o tipo
 * (Máquina ou Gramática), aplicando filtros por subtipo.
 *
 * O componente encapsula:
 * - Listagem de exercícios disponíveis
 * - Filtro por tipo
 * - Seleção de exercício para simulação
 *
 * @author Willian Brandao
 *
 * @param {Object} props Propriedades do componente.
 * @param {boolean} props.open Controla a abertura do diálogo.
 * @param {Function} props.onClose Função para fechar o diálogo.
 * @param {string} props.language Idioma atual ('pt' ou 'en').
 * @param {string} props.type Tipo de exercício ('MACHINE' ou 'GRAMMAR').
 * @param {Function} props.onSelect Callback executado ao selecionar um exercício.
 * @param {Function} props.setAutomatonType Define o tipo de autômato no contexto global.
 *
 * @returns {JSX.Element|null} Componente renderizado ou null em caso de tipo inválido.
 */
export default function UserExerciseSelector(props) {
  const labels = exerciseConstants.title;
  const module = EXERCISE_MODULES[props.type];

  if (!module) {
    console.error("Tipo de exercício inválido:", props.type);
    return null;
  }

  const subTypeLabel = labels[module.labelKey][props.language];

  return (
    <ExerciseSelector {...props} isAdmin={false}>
      {({ exercises }) => {
        const { selectedType, setSelectedType, filteredExercises } =
          useExerciseFilter(exercises);

        const dialogTitle = `${subTypeLabel} ${labels.exerciseList[props.language]}`;
        /**
         * Trata a seleção de um exercício.
         *
         * Para exercícios do tipo MACHINE, define o tipo de autômato
         * no contexto global antes de propagar a seleção.
         *
         * @param {Object} ex Exercício selecionado.
         * @param {string} ex.id Identificador do exercício.
         * @param {string} [ex.automatonType] Tipo do autômato (quando aplicável).
         */
        const handleSelection = (ex) => {
          if (props.type === "MACHINE" && ex.automatonType) {
            props.setAutomatonType(ex.automatonType);
          }
          props.onSelect(ex.id);
        };

        return (
          <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle disableTypography={true}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  style={{ fontWeight: "bold", color: "#3f51b5" }}
                >
                  {dialogTitle}
                </Typography>
              </Box>
            </DialogTitle>

            <Divider />

            <DialogContent dividers>
              {/* Renderiza o seletor de tipos antes da lista */}
              <ExerciseFilterHeader
                type={props.type}
                language={props.language}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
              />

              {/* Passa a lista filtrada para o componente de visualização */}
              <ExerciseList
                exercises={filteredExercises}
                language={props.language}
                onSelect={handleSelection}
              />
            </DialogContent>
          </Dialog>
        );
      }}
    </ExerciseSelector>
  );
}
